import React, { useState, useCallback, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getCurrentAccDetailsSelector } from '../../store/selectors/app.selector'
import CardLessService from '../../services/cardLessCash.service'
import { NeoOTP, NeoSuccess } from '../../components/FormComponents'
import Loading from 'custom/assets/loaders/Loading'
import AccountDisplay from '../../components/account-display/account-display'
import NeoInput from '../../components/NeoInput/NeoInput'
import InputError from '../../components/InputError/InputError'
import NeoButton from '../../components/NeoButton/NeoButton'
import LayoutWithAccountCard from '../Layout/layout-account-card'
import { CardLessSchema } from '../../schemas/cardLessCash'
import { CARD_LESS_CASH_KEYS, BUTTON_KEYS } from '../../utils/translation-constants'
import { useTranslation } from 'react-i18next'
import TermsAndCondition from '../../components/TermsAndConditions/TermsAndCondition'

export default function GenerateToken() {
  const {t} = useTranslation()
  const history = useHistory()
  const [stage, setStage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [verifyDetails, setVerifyDetails] = useState({})
  const [otp, setOtp] = useState('')
  const [formValues, setFormValues] = useState({
    remarks: '',
    mobileNumber: '',
    expiry: '',
    transferAmount: '',
  })
  const currentAccountDetail = useSelector(getCurrentAccDetailsSelector) || {}

  const onVerify = useCallback(
    async (values) => {
      try {
        setLoading(true)
        const req = {
          amount: values.transferAmount,
          mobileNumber: values.mobileNumber,
          expiry: values.expiry,
          srcAccount: currentAccountDetail.accountNo,
          srcAccBranch: currentAccountDetail.brnCode,
          srcAccCcy: currentAccountDetail.currency,
          txnCurrency: currentAccountDetail.currency,
          remarks: values.remarks,
        }
        setFormValues(values)
        const response = await CardLessService.initVerify(req)
        if (response.status === 200) {
          setOtpEnabled(response.data['otpEnable'])
          setVerifyDetails(response.data)
          setLoading(false)
          setStage(2)
        }
      } catch (err) {
        setLoading(false)
      }
    },
    [currentAccountDetail],
  )

  const onConfirm = useCallback(async () => {
    try {
      setLoading(true)
      const req = {
        transferType: '',
        amount: formValues.transferAmount,
        mobileNumber: formValues.mobileNumber,
        expiry: formValues.expiry,
        srcAccount: currentAccountDetail.accountNo,
        srcAccBranch: currentAccountDetail.brnCode,
        srcAccCcy: currentAccountDetail.currency,
        txnCurrency: currentAccountDetail.currency,
        remarks: formValues.remarks,
        otp: otp,
        randomKey:verifyDetails['randomKey']
      }
      const response = await CardLessService.initConfirm(req)
      if (response.status === 200) {
        setLoading(false)
        setMessage(response.data['message'])
        setStage(4)
      }
    } catch (err) {
      setLoading(false)
      setOtp('')
    }
  }, [currentAccountDetail, formValues, otp,verifyDetails])

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderForm()
      case 2:
        return renderConfirmation()
      case 3:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onConfirm} loading={loading} />
      case 4:
        return <NeoSuccess message={message} linkPath='/cardLessCash' />
      default:
        return renderForm()
    }
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  function renderForm() {
    return (
      <>
        <AccountDisplay currentAccountDetail={currentAccountDetail} />
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={CardLessSchema}
          onSubmit={onVerify}>
          {({ isSubmitting, handleChange, errors, values, touched, setFieldValue }) => (
            <Form>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='1'
                    text={t(CARD_LESS_CASH_KEYS.MOBILE_NO)}
                    onChange={(value) => {
                      setFieldValue('mobileNumber', value?.value)
                    }}
                    mode='onlyNumber'
                    name='mobileNumber'
                  />
                  <InputError text={errors.mobileNumber} touched={touched.mobileNumber} />
                </div>

                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='2'
                    text={t(CARD_LESS_CASH_KEYS.AMOUNT)}
                    name='amount'
                    onChange={(values) => {
                      setFieldValue('transferAmount', values.value)
                    }}
                    mode='amount'
                  />
                  <InputError text={errors.transferAmount} touched={touched.transferAmount} />
                </div>

                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='3'
                    text={t(CARD_LESS_CASH_KEYS.EXPIRY)}
                    onChange={(value) => {
                      setFieldValue('expiry', value?.value)
                    }}
                    mode='onlyNumber'
                    name='expiry'
                    minLength={1}
                    maxLength={2}
                  />
                  <InputError text={errors.expiry} touched={touched.expiry} />
                </div>

                <div className='form-group col-12'>
                  <NeoInput tabIndex='4' text={t(CARD_LESS_CASH_KEYS.REMARKS)} onChange={handleChange} name='remarks' />
                  <InputError text={errors.remarks} touched={touched.remarks} />
                </div>
              </div>
              <div className='mt-3'>
                {loading ? (
                  renderLoading()
                ) : (
                  <NeoButton tabIndex='5' type='submit' mode='newDesign' text={t(CARD_LESS_CASH_KEYS.SUBMIT)} />
                )}
              </div>
              <div className='back-cancel-btn-container'>
              <button tabIndex={'6'} className='back-cancel-btn btn btn-sm new-design-warning-text-color'  onClick={() => history.goBack()}>
                {t(CARD_LESS_CASH_KEYS.CANCEL)}
              </button>
              </div>
            </Form>
          )}
        </Formik>
      </>
    )
  }

  function renderConfirmation() {
    return (
      <>
        <AccountDisplay currentAccountDetail={currentAccountDetail} />
        <div className='d-flex flex-column confirmation'>
          <div>
            <h6 className='label'>{t(CARD_LESS_CASH_KEYS.MOBILE_NO)}</h6>
            <h6 className='value'>{formValues?.mobileNumber}</h6>
          </div>
          <div>
            <h6 className='label'>{t(CARD_LESS_CASH_KEYS.AMOUNT)}</h6>
            <h6 className='value'>{formValues?.amount}</h6>
          </div>
          <div>
            <h6 className='label'>{t(CARD_LESS_CASH_KEYS.CHARGES)}</h6>
            <h6 className='value'>{verifyDetails?.charges}</h6>
          </div>

          <div>
            <h6 className='label'>{t(CARD_LESS_CASH_KEYS.EXPIRY)}</h6>
            <h6 className='value'>{formValues?.expiry}</h6>
          </div>
          <div>
            <h6 className='label'>{t(CARD_LESS_CASH_KEYS.CURRENCY)}</h6>
            <h6 className='value'>{currentAccountDetail?.currency}</h6>
          </div>

          <div>
            <h6 className='label'>{t(CARD_LESS_CASH_KEYS.REMARKS)}</h6>
            <h6 className='value'>{formValues?.remarks}</h6>
          </div>

          <div>
          <TermsAndCondition module='CARDLESSCASHOUT' />
        </div>

          <div className='mt-3'>
            {loading ? (
              renderLoading()
            ) : (
              <NeoButton
                onClick={() => (otpEnabled ? setStage(3) : onConfirm())}
                type='button'
                mode='newDesign'
                text={t(BUTTON_KEYS.CONFIRM)}
              />
            )}
          </div>
          <div className='back-cancel-style' onClick={() => history.goBack()}>
            {t(BUTTON_KEYS.CANCEL)}
          </div>
        </div>
      </>
    )
  }

  return (
    <LayoutWithAccountCard breadCumKey='CARD_LESS_CASH_GENERATE_TOKEN'>
      <div className='new-design-form-style'>
        <div className='heading-style'>{t(CARD_LESS_CASH_KEYS.GENERATE_TOKEN)}</div>
        {renderSwitch()}
      </div>
    </LayoutWithAccountCard>
  )
}
